/**
 * MaintenanceStartDialog — start an account-maintenance request from the customer's full record (Bill 2026-10-17:
 * "Maintenance items, all or buildable from existing account opening parts. Build it, wire it, end to end").
 *
 * The officer picks the change on one account; the server builds the request from Core (owners, beneficiaries,
 * product) and opens it as a maintenance application. From there it's the opening flow: review, the customer signs
 * the forms the change needs, Core is updated, follow-up tasks are created. Each action shows only when an
 * administrator has switched it on.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { AlertTriangle, FileSignature, ShieldCheck } from '@retail-ops/panel-shell/fi';
import type { AccountParty, MaintenanceActionCode, MaintenanceActionInfo } from '@retail-ops/shared-types';
import { Button, ModalShell } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import { BeneficiaryEditorModal, type BenefDraft } from '@/modules/RAO/components/BeneficiaryEditorModal';
import './maintenance.css';

export interface MaintenanceTarget {
  acctId: string;
  acctType: string;
  /** What the officer sees for the account, e.g. "Free Checking ··7875". */
  label: string;
}

/** What each action does, in plain words, for the dialog's lead. */
const HOW: Record<MaintenanceActionCode, { what: string; signs: string }> = {
  'debit-card': { what: 'A debit card is ordered for each owner you choose. Print and activate it on the finished screen, as at account opening.', signs: 'Nothing to sign.' },
  'order-checks': { what: 'Records the check order and creates the Order checks task. Checks are ordered from the vendor as today.', signs: 'The owners sign a new signature card (the bank requires one when checks are ordered).' },
  pod: { what: 'Replaces the account\'s POD beneficiaries with the designation you enter.', signs: 'The owners sign the POD designation form.' },
  'joint-add': { what: 'Adds a joint owner. You add the person on the next screen, the same way as at account opening (search, or new customer with ID, OFAC and LexisNexis checks).', signs: 'Every owner, including the new one, signs a new signature card.' },
  'joint-remove': { what: 'Removes a joint owner from the account in Core Director.', signs: 'The owners who remain sign a new signature card.' },
  'od-protection': { what: 'Changes the customer\'s Reg E choice for ATM and everyday debit card overdrafts.', signs: 'The primary owner signs the overdraft coverage (A-9) form.' },
  'od-limit': { what: 'Changes the account\'s overdraft limit in Core Director.', signs: 'The primary owner signs the overdraft coverage (A-9) form showing the new limit.' },
  statements: { what: 'Turns e-statements on or off. E-statements need online banking, as at account opening.', signs: 'The customer is given the E-SIGN disclosure.' },
};

function ownersOf(parties: ReadonlyArray<AccountParty>, cif: string): AccountParty[] {
  return parties.filter((p) => p.relCode === 'AO' || p.relCode === '' || p.custId === cif);
}

export function MaintenanceStartDialog({ open, action, target, cif, onClose, testCustomersOnly = true }: {
  open: boolean;
  /** Maintenance limited to test customers (server-enforced; this only explains it). */
  testCustomersOnly?: boolean;
  action: MaintenanceActionInfo | null;
  target: MaintenanceTarget | null;
  cif: string;
  onClose: () => void;
}): JSX.Element | null {
  const client = useApiClient();
  const navigate = useNavigate();
  const { config } = useConfig();
  const [parties, setParties] = useState<AccountParty[] | null>(null);
  const [partiesError, setPartiesError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-action inputs.
  const [cardholders, setCardholders] = useState<Set<string>>(new Set());
  const [design, setDesign] = useState('101');
  const [checkDesign, setCheckDesign] = useState('');
  const [startNumber, setStartNumber] = useState('1001');
  const [quantity, setQuantity] = useState('60');
  const [benefs, setBenefs] = useState<ReadonlyArray<BenefDraft> | null>(null);
  const [benefOpen, setBenefOpen] = useState(false);
  const [removeCif, setRemoveCif] = useState('');
  const [regE, setRegE] = useState<'optIn' | 'optOut' | ''>('');
  const [newLimit, setNewLimit] = useState('');
  const [eStatements, setEStatements] = useState<'on' | 'off' | ''>('');

  useEffect(() => {
    if (!open || !target) return undefined;
    setError(null); setParties(null); setPartiesError(null); setBenefs(null); setRemoveCif(''); setRegE(''); setNewLimit(''); setEStatements('');
    const ctrl = new AbortController();
    client.accounts.parties({ acctId: target.acctId, acctType: target.acctType }, { signal: ctrl.signal })
      .then((r) => {
        if (ctrl.signal.aborted) return;
        if (!r.success) { setPartiesError(r.error ?? 'Core Director didn\'t return the account\'s owners.'); return; }
        setParties([...r.parties]);
        setCardholders(new Set(ownersOf(r.parties, cif).map((p) => p.custId)));
      })
      .catch((e: unknown) => { if (!ctrl.signal.aborted) setPartiesError(e instanceof Error ? e.message : 'Could not read the account\'s owners.'); });
    return () => ctrl.abort();
  }, [open, target, client, cif]);

  const checkDesigns = useMemo(() => {
    const raw = (config.systemSettings as Record<string, unknown> | undefined)?.['checks.designs'];
    try {
      const parsed: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed)
        ? parsed.map((o) => ({ code: String((o as Record<string, unknown>).code ?? ''), name: String((o as Record<string, unknown>).displayName ?? (o as Record<string, unknown>).code ?? '') })).filter((d) => d.code)
        : [];
    } catch { return []; }
  }, [config.systemSettings]);
  const cardDesigns = useMemo(() => (config.cardTypes ?? []).map((c) => {
    const anyC = c as unknown as Record<string, unknown>;
    return { code: String(c.code), name: c.name || (typeof anyC['description'] === 'string' ? anyC['description'] as string : `Design ${c.code}`) };
  }), [config.cardTypes]);

  if (!open || !action || !target) return null;
  const how = HOW[action.code];
  const owners = parties ? ownersOf(parties, cif) : [];
  const joints = parties ? parties.filter((p) => p.relCode === 'AO' && p.custId !== cif) : [];
  const existingPods = parties ? parties.filter((p) => p.relCode === 'BP' || p.relCode === 'BC') : [];
  const initialBenefs: BenefDraft[] = existingPods.map((p) => ({
    id: `existing-${p.custId}`, cifId: p.custId, role: p.relCode === 'BC' ? 'contingent' : 'primary',
    mechanism: p.relCode === 'BC' ? 'CustRelAdd-BC' : 'CustRelAdd-BP',
    firstName: p.firstName ?? p.name, lastName: p.lastName ?? '', relationship: '', percentage: 0,
  } as BenefDraft));
  const designation = benefs ?? initialBenefs;

  const ready = ((): string | null => {
    if (!parties) return partiesError ? 'The account\'s owners couldn\'t be read.' : 'Reading the account from Core Director…';
    switch (action.code) {
      case 'debit-card': return cardholders.size === 0 ? 'Choose at least one cardholder.' : null;
      case 'order-checks': return !checkDesign ? 'Choose a check design.' : Number(quantity) < 25 || Number(quantity) > 500 ? 'Quantity is 25 to 500.' : null;
      case 'pod': return benefs === null ? 'Enter the new beneficiary designation.' : null;
      case 'joint-remove': return !removeCif ? 'Choose the joint owner to remove.' : null;
      case 'od-protection': return !regE ? 'Choose opt in or opt out.' : null;
      case 'od-limit': return newLimit.trim() === '' || Number.isNaN(Number(newLimit)) || Number(newLimit) < 0 ? 'Enter the new overdraft limit.' : null;
      case 'statements': return !eStatements ? 'Choose e-statements on or off.' : null;
      default: return null;
    }
  })();

  const start = async (): Promise<void> => {
    if (ready) return;
    setBusy(true);
    setError(null);
    const changes: Record<string, unknown> = {};
    if (action.code === 'debit-card') {
      changes.cardholders = [...cardholders];
      changes.designs = Object.fromEntries([...cardholders].map((c) => [c, design]));
    }
    if (action.code === 'order-checks') changes.order = { design: checkDesign, startNumber: Number(startNumber) || 1001, quantity: Number(quantity) || 60 };
    if (action.code === 'joint-remove') changes.removeCif = removeCif;
    if (action.code === 'od-protection') changes.regE = regE;
    if (action.code === 'od-limit') changes.newLimit = Number(newLimit);
    if (action.code === 'statements') changes.eStatements = eStatements === 'on';
    try {
      const res = await client.retail.maintenance.create({
        action: action.code, acctId: target.acctId, acctType: target.acctType, cif, changes,
        ...(action.code === 'pod' ? { beneficiaries: designation.map((b) => ({ ...b } as unknown as Record<string, unknown>)) } : {}),
      });
      if (!res.success || !res.cartId) { setError(res.error ?? 'The request couldn\'t be started.'); return; }
      onClose();
      navigate(`/rao/${encodeURIComponent(res.cartId)}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The request couldn\'t be started.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell open onClose={onClose} size="md" title={`${action.title} · ${target.label}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void start()} disabled={busy || ready !== null} title={ready ?? undefined}>
            {busy ? 'Starting…' : 'Start request'}
          </Button>
        </>
      }>
      <div className="mt-dialog">
        <p className="mt-dialog__lead">{how.what}</p>
        <div className="mt-dialog__facts">
          <span><FileSignature size={14} aria-hidden /> {how.signs}</span>
          <span><ShieldCheck size={14} aria-hidden /> Core Director changes only after the request is submitted{action.documents.length ? ' and signed' : ''}.</span>
        </div>
        {testCustomersOnly && (
          <div className="mt-dialog__warn" role="note">
            <AlertTriangle size={14} aria-hidden /> Account maintenance is limited to test customers for now (last name Test with a 666 SSN/TIN or CIF). A real customer's account is refused.
          </div>
        )}
        {!action.coreProven && (
          <div className="mt-dialog__warn" role="note">
            <AlertTriangle size={14} aria-hidden /> This change hasn't been proven against Core Director yet.
          </div>
        )}
        {partiesError && <div className="mt-dialog__error" role="alert">{partiesError}</div>}
        {!parties && !partiesError && <div className="mt-dialog__loading">Reading the account from Core Director…</div>}

        {parties && action.code === 'debit-card' && (
          <>
            <fieldset className="mt-field">
              <legend>Who is the card for?</legend>
              {owners.map((o) => (
                <label key={o.custId} className="mt-check">
                  <input type="checkbox" checked={cardholders.has(o.custId)}
                    onChange={() => setCardholders((s) => { const n = new Set(s); if (n.has(o.custId)) n.delete(o.custId); else n.add(o.custId); return n; })} />
                  {o.name} <span className="mt-muted">{o.relLabel ?? ''}</span>
                </label>
              ))}
            </fieldset>
            {cardDesigns.length > 0 && (
              <label className="mt-field">
                <span>Card design</span>
                <select value={design} onChange={(e) => setDesign(e.target.value)}>
                  {cardDesigns.map((d) => <option key={d.code} value={d.code}>{d.name}</option>)}
                </select>
              </label>
            )}
          </>
        )}

        {parties && action.code === 'order-checks' && (
          <div className="mt-grid">
            <label className="mt-field">
              <span>Design</span>
              <select value={checkDesign} onChange={(e) => setCheckDesign(e.target.value)}>
                <option value="">Choose…</option>
                {checkDesigns.map((d) => <option key={d.code} value={d.code}>{d.name}</option>)}
              </select>
            </label>
            <label className="mt-field"><span>Starting #</span><input inputMode="numeric" value={startNumber} onChange={(e) => setStartNumber(e.target.value.replace(/\D/g, ''))} /></label>
            <label className="mt-field"><span>Quantity</span><input inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value.replace(/\D/g, ''))} /></label>
          </div>
        )}

        {parties && action.code === 'pod' && (
          <div className="mt-field">
            <span>New designation</span>
            {existingPods.length > 0 && (
              <p className="mt-muted">On file now: {existingPods.map((p) => `${p.name} (${p.relCode === 'BC' ? 'contingent' : 'primary'})`).join(', ')}. What you enter replaces them.</p>
            )}
            <ul className="mt-list">
              {designation.length === 0 ? <li className="mt-muted">No beneficiaries (removes them all).</li>
                : designation.map((b) => <li key={b.id}>{`${b.firstName} ${b.lastName}`.trim()} · {b.role} · {b.percentage}%</li>)}
            </ul>
            <Button variant="ghost" onClick={() => setBenefOpen(true)}>{benefs === null ? 'Enter the designation…' : 'Change the designation…'}</Button>
            <BeneficiaryEditorModal open={benefOpen} title="POD beneficiaries" initial={designation}
              acctType={(target.acctType as '10' | '20')} onClose={() => setBenefOpen(false)}
              onCommit={(d) => { setBenefs(d); setBenefOpen(false); }} />
          </div>
        )}

        {parties && action.code === 'joint-remove' && (
          <fieldset className="mt-field">
            <legend>Which joint owner?</legend>
            {joints.length === 0 ? <p className="mt-muted">This account has no joint owners in Core Director.</p>
              : joints.map((o) => (
                <label key={o.custId} className="mt-check">
                  <input type="radio" name="mt-remove" checked={removeCif === o.custId} onChange={() => setRemoveCif(o.custId)} /> {o.name}
                </label>
              ))}
          </fieldset>
        )}

        {parties && action.code === 'od-protection' && (
          <fieldset className="mt-field">
            <legend>The customer's choice</legend>
            <label className="mt-check"><input type="radio" name="mt-rege" checked={regE === 'optIn'} onChange={() => setRegE('optIn')} /> Opt in: cover ATM and everyday debit card overdrafts</label>
            <label className="mt-check"><input type="radio" name="mt-rege" checked={regE === 'optOut'} onChange={() => setRegE('optOut')} /> Opt out: decline them</label>
          </fieldset>
        )}

        {parties && action.code === 'od-limit' && (
          <label className="mt-field">
            <span>New overdraft limit ($)</span>
            <input inputMode="decimal" value={newLimit} onChange={(e) => setNewLimit(e.target.value.replace(/[^\d.]/g, ''))} placeholder="e.g. 300" />
            <span className="mt-muted">A limit of $500 or more needs a supervisor's approval (the bank's rule).</span>
          </label>
        )}

        {parties && action.code === 'statements' && (
          <fieldset className="mt-field">
            <legend>E-statements</legend>
            <label className="mt-check"><input type="radio" name="mt-stmt" checked={eStatements === 'on'} onChange={() => setEStatements('on')} /> On (e-mail), paper statements stop</label>
            <label className="mt-check"><input type="radio" name="mt-stmt" checked={eStatements === 'off'} onChange={() => setEStatements('off')} /> Off (paper statements)</label>
          </fieldset>
        )}

        {error && <div className="mt-dialog__error" role="alert">{error}</div>}
      </div>
    </ModalShell>
  );
}
